/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    Alert,
    CircularProgress,
    Chip,
    Card,
    CardContent,
    CardMedia,
    Rating,
    Checkbox,
    FormControlLabel,
    Slider,
    IconButton,
    Collapse,
    Paper,
    Grid,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { Autocomplete, Popper } from '@mui/material';
import {
    Hotel,
    Search,
    LocationOn,
    Person,
    CheckCircle,
    Close,
    FilterList,
    Pool,
    Wifi,
    FreeBreakfast,
    LocalParking,
    FitnessCenter,
    Spa,
    ExpandLess,
    Recommend,
    MeetingRoom,
    Add,
    Remove,
    Edit,
    Group as GroupIcon,
} from '@mui/icons-material';

// ============================================================================
// TYPES
// ============================================================================

interface HotelSearchDialogProps {
    open: boolean;
    onClose: () => void;
    tripId: string;
    destination: string;
    checkIn: string;
    checkOut: string;
    guests?: number;
    rooms?: number;
    isGroupTrip?: boolean;
    groupSize?: number;
    onHotelsSelected: (hotels: HotelResult[]) => Promise<void>;
}

interface HotelResult {
    id: string;
    name: string;
    location: string;
    address: string;
    rating: number;
    reviewCount: number;
    reviewScore: number;
    pricePerNight: number;
    totalPrice: number;
    currency: string;
    image: string;
    amenities: string[];
    freeCancellation: boolean;
    payLater: boolean;
    recommended?: boolean;
    starRating: number;
}

// ============================================================================
// STEPPER COMPONENT
// ============================================================================

interface StepperInputProps {
    value: number;
    onChange: (value: number) => void;
    min?: number;
    max?: number;
    label: string;
    icon?: React.ReactNode;
    compact?: boolean;
}

const StepperInput: React.FC<StepperInputProps> = ({
    value,
    onChange,
    min = 1,
    max = 9,
    label,
    icon,
    compact = false,
}) => {
    const handleDecrement = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (value > min) onChange(value - 1);
    };

    const handleIncrement = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (value < max) onChange(value + 1);
    };

    const buttonSize = compact ? 28 : 32;

    return (
        <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            border: '1px solid rgba(0, 0, 0, 0.23)',
            borderRadius: 2,
            px: compact ? 1 : 1.5,
            py: compact ? 0.5 : 1,
            bgcolor: 'white',
            minHeight: 56,
            '&:hover': { borderColor: 'rgba(0, 0, 0, 0.87)' },
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                {icon}
                <Typography variant={compact ? 'caption' : 'body2'} color="text.secondary">
                    {label}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: compact ? 0.5 : 1 }}>
                <IconButton
                    size="small"
                    onClick={handleDecrement}
                    disabled={value <= min}
                    sx={{ bgcolor: '#f5f5f5', width: buttonSize, height: buttonSize }}
                >
                    <Remove fontSize="small" />
                </IconButton>
                <Typography sx={{ minWidth: 24, textAlign: 'center', fontWeight: 600 }}>
                    {value}
                </Typography>
                <IconButton
                    size="small"
                    onClick={handleIncrement}
                    disabled={value >= max}
                    sx={{ bgcolor: '#f5f5f5', width: buttonSize, height: buttonSize }}
                >
                    <Add fontSize="small" />
                </IconButton>
            </Box>
        </Box>
    );
};

// ============================================================================
// MOCK DATA
// ============================================================================

const generateMockHotels = (destination: string, checkIn: string, checkOut: string, rooms: number): HotelResult[] => {
    const nights = checkIn && checkOut 
        ? Math.ceil((new Date(checkOut).getTime() - new Date(checkIn).getTime()) / (1000 * 60 * 60 * 24))
        : 1;
    
    const hotels: HotelResult[] = [
        {
            id: 'hotel-1',
            name: `${destination} Grand Hotel`,
            location: `Downtown ${destination}`,
            address: `123 Main Street, ${destination}`,
            rating: 4.5,
            reviewCount: 2847,
            reviewScore: 8.9,
            pricePerNight: 189 * rooms,
            totalPrice: 189 * nights * rooms,
            currency: 'USD',
            image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400',
            amenities: ['pool', 'wifi', 'breakfast', 'parking', 'gym', 'spa'],
            freeCancellation: true,
            payLater: true,
            recommended: true,
            starRating: 5,
        },
        {
            id: 'hotel-2',
            name: `Hilton ${destination}`,
            location: `Business District, ${destination}`,
            address: `456 Commerce Ave, ${destination}`,
            rating: 4.3,
            reviewCount: 1923,
            reviewScore: 8.5,
            pricePerNight: 159 * rooms,
            totalPrice: 159 * nights * rooms,
            currency: 'USD',
            image: 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=400',
            amenities: ['wifi', 'breakfast', 'parking', 'gym'],
            freeCancellation: true,
            payLater: false,
            starRating: 4,
        },
        {
            id: 'hotel-3',
            name: `Marriott ${destination} Resort`,
            location: `Beachfront, ${destination}`,
            address: `789 Ocean Drive, ${destination}`,
            rating: 4.7,
            reviewCount: 3156,
            reviewScore: 9.2,
            pricePerNight: 249 * rooms,
            totalPrice: 249 * nights * rooms,
            currency: 'USD',
            image: 'https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=400',
            amenities: ['pool', 'wifi', 'breakfast', 'parking', 'gym', 'spa'],
            freeCancellation: true,
            payLater: true,
            starRating: 5,
        },
        {
            id: 'hotel-4',
            name: `Holiday Inn Express ${destination}`,
            location: `Near Airport, ${destination}`,
            address: `101 Airport Blvd, ${destination}`,
            rating: 4.0,
            reviewCount: 892,
            reviewScore: 7.8,
            pricePerNight: 99 * rooms,
            totalPrice: 99 * nights * rooms,
            currency: 'USD',
            image: 'https://images.unsplash.com/photo-1564501049412-61c2a3083791?w=400',
            amenities: ['wifi', 'breakfast', 'parking'],
            freeCancellation: true,
            payLater: false,
            starRating: 3,
        },
        {
            id: 'hotel-5',
            name: `Boutique Hotel ${destination}`,
            location: `Historic District, ${destination}`,
            address: `222 Heritage Lane, ${destination}`,
            rating: 4.8,
            reviewCount: 456,
            reviewScore: 9.4,
            pricePerNight: 179 * rooms,
            totalPrice: 179 * nights * rooms,
            currency: 'USD',
            image: 'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=400',
            amenities: ['wifi', 'breakfast', 'spa'],
            freeCancellation: false,
            payLater: true,
            starRating: 4,
        },
    ];
    
    return hotels;
};

const amenityIcons: Record<string, React.ReactElement> = {
    pool: <Pool sx={{ fontSize: 14 }} />,
    wifi: <Wifi sx={{ fontSize: 14 }} />,
    breakfast: <FreeBreakfast sx={{ fontSize: 14 }} />,
    parking: <LocalParking sx={{ fontSize: 14 }} />,
    gym: <FitnessCenter sx={{ fontSize: 14 }} />,
    spa: <Spa sx={{ fontSize: 14 }} />,
};
// ============================================================================
// DESTINATION DATA & AUTOCOMPLETE
// ============================================================================

interface DestinationOption {
    name: string;
    region: string;
    country: string;
    type: 'city' | 'region' | 'landmark';
    label: string;
  }
  
  const DESTINATIONS: DestinationOption[] = [
    // US Cities
    { name: 'New York', region: 'New York', country: 'US', type: 'city', label: 'New York, NY, USA' },
    { name: 'Manhattan', region: 'New York', country: 'US', type: 'region', label: 'Manhattan, New York, USA' },
    { name: 'Los Angeles', region: 'California', country: 'US', type: 'city', label: 'Los Angeles, CA, USA' },
    { name: 'San Francisco', region: 'California', country: 'US', type: 'city', label: 'San Francisco, CA, USA' },
    { name: 'Chicago', region: 'Illinois', country: 'US', type: 'city', label: 'Chicago, IL, USA' },
    { name: 'Miami', region: 'Florida', country: 'US', type: 'city', label: 'Miami, FL, USA' },
    { name: 'Miami Beach', region: 'Florida', country: 'US', type: 'region', label: 'Miami Beach, FL, USA' },
    { name: 'South Beach', region: 'Florida', country: 'US', type: 'region', label: 'South Beach, Miami, FL, USA' },
    { name: 'Orlando', region: 'Florida', country: 'US', type: 'city', label: 'Orlando, FL, USA' },
    { name: 'Las Vegas', region: 'Nevada', country: 'US', type: 'city', label: 'Las Vegas, NV, USA' },
    { name: 'The Strip', region: 'Nevada', country: 'US', type: 'landmark', label: 'The Strip, Las Vegas, NV, USA' },
    { name: 'Seattle', region: 'Washington', country: 'US', type: 'city', label: 'Seattle, WA, USA' },
    { name: 'Boston', region: 'Massachusetts', country: 'US', type: 'city', label: 'Boston, MA, USA' },
    { name: 'Washington', region: 'District of Columbia', country: 'US', type: 'city', label: 'Washington, DC, USA' },
    { name: 'San Diego', region: 'California', country: 'US', type: 'city', label: 'San Diego, CA, USA' },
    { name: 'Denver', region: 'Colorado', country: 'US', type: 'city', label: 'Denver, CO, USA' },
    { name: 'Austin', region: 'Texas', country: 'US', type: 'city', label: 'Austin, TX, USA' },
    { name: 'Nashville', region: 'Tennessee', country: 'US', type: 'city', label: 'Nashville, TN, USA' },
    { name: 'New Orleans', region: 'Louisiana', country: 'US', type: 'city', label: 'New Orleans, LA, USA' },
    { name: 'Honolulu', region: 'Hawaii', country: 'US', type: 'city', label: 'Honolulu, HI, USA' },
    { name: 'Waikiki', region: 'Hawaii', country: 'US', type: 'region', label: 'Waikiki, Honolulu, HI, USA' },
    // Europe
    { name: 'London', region: 'England', country: 'UK', type: 'city', label: 'London, England, UK' },
    { name: 'Paris', region: 'Île-de-France', country: 'France', type: 'city', label: 'Paris, France' },
    { name: 'Rome', region: 'Lazio', country: 'Italy', type: 'city', label: 'Rome, Italy' },
    { name: 'Barcelona', region: 'Catalonia', country: 'Spain', type: 'city', label: 'Barcelona, Spain' },
    { name: 'Amsterdam', region: 'North Holland', country: 'Netherlands', type: 'city', label: 'Amsterdam, Netherlands' },
    { name: 'Berlin', region: 'Berlin', country: 'Germany', type: 'city', label: 'Berlin, Germany' },
    { name: 'Tokyo', region: 'Kanto', country: 'Japan', type: 'city', label: 'Tokyo, Japan' },
    { name: 'Singapore', region: 'Singapore', country: 'Singapore', type: 'city', label: 'Singapore' },
    { name: 'Dubai', region: 'Dubai', country: 'UAE', type: 'city', label: 'Dubai, UAE' },
    { name: 'Cancún', region: 'Quintana Roo', country: 'Mexico', type: 'city', label: 'Cancún, Mexico' },
    { name: 'Toronto', region: 'Ontario', country: 'Canada', type: 'city', label: 'Toronto, Ontario, Canada' },
    { name: 'Vancouver', region: 'British Columbia', country: 'Canada', type: 'city', label: 'Vancouver, BC, Canada' },
  ];
  
  const filterDestinations = (query: string): DestinationOption[] => {
    if (!query || query.length < 2) return [];
    const normalizedQuery = query.toLowerCase().trim();
    
    return DESTINATIONS.filter(dest => {
      const searchFields = [
        dest.name.toLowerCase(),
        dest.region.toLowerCase(),
        dest.country.toLowerCase(),
        dest.label.toLowerCase(),
      ];
      return searchFields.some(field => field.includes(normalizedQuery));
    }).slice(0, 8);
  };
  
  const AutocompletePopper = (props: any) => (
    <Popper {...props} placement="bottom-start" style={{ zIndex: 1500 }} />
  );
// ============================================================================
// MAIN COMPONENT
// ============================================================================

const HotelSearchDialog: React.FC<HotelSearchDialogProps> = ({
    open,
    onClose,
    tripId,
    destination,
    checkIn,
    checkOut,
    guests = 2,
    rooms = 1,
    isGroupTrip = false,
    groupSize,
    onHotelsSelected,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Search form state
    const [searchDestination, setSearchDestination] = useState(destination || '');
    const [searchCheckIn, setSearchCheckIn] = useState(checkIn ? checkIn.split('T')[0] : '');
    const [searchCheckOut, setSearchCheckOut] = useState(checkOut ? checkOut.split('T')[0] : '');
    const [searchGuests, setSearchGuests] = useState(guests);
    const [searchRooms, setSearchRooms] = useState(rooms);
    const [hotelName, setHotelName] = useState('');
    
    // UI state
    const [searchFormExpanded, setSearchFormExpanded] = useState(true);

    // Results state
    const [searching, setSearching] = useState(false);
    const [results, setResults] = useState<HotelResult[]>([]);
    const [hasSearched, setHasSearched] = useState(false);
    
    // Selection state
    const [selectedHotels, setSelectedHotels] = useState<string[]>([]);
    const [maxSelections] = useState(5);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    
    // Filter state
    const [showFilters, setShowFilters] = useState(false);
    const [priceRange, setPriceRange] = useState<number[]>([0, 500]);
    const [minRating, setMinRating] = useState(0);
    const [freeCancellationOnly, setFreeCancellationOnly] = useState(false);

    // Get today's date for min attribute
    const today = new Date().toISOString().split('T')[0];

    // Update search fields when props change
    useEffect(() => {
        if (open) {
            setSearchDestination(destination || '');
            setSearchCheckIn(checkIn ? checkIn.split('T')[0] : '');
            setSearchCheckOut(checkOut ? checkOut.split('T')[0] : '');
            // Smart defaults for group trips
            if (isGroupTrip && groupSize) {
                setSearchGuests(groupSize);
                const suggestedRooms = Math.min(Math.ceil(groupSize / 2), 10);
                setSearchRooms(suggestedRooms);
            } else {
                setSearchGuests(guests);
                setSearchRooms(rooms);
            }
            setSelectedHotels([]);
            setResults([]);
            setHasSearched(false);
            setError('');
            setSearchFormExpanded(true);
        }
    }, [open, destination, checkIn, checkOut, guests, rooms, isGroupTrip, groupSize]);

    // Auto-update checkout date min when checkin changes
    useEffect(() => {
        if (searchCheckOut && searchCheckIn && searchCheckOut <= searchCheckIn) {
            const nextDay = new Date(searchCheckIn);
            nextDay.setDate(nextDay.getDate() + 1);
            setSearchCheckOut(nextDay.toISOString().split('T')[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchCheckIn]);

    const handleSearch = async () => {
        if (!searchDestination.trim()) {
            setError('Please enter a destination');
            return;
        }
        if (!searchCheckIn) {
            setError('Please select a check-in date');
            return;
        }
        if (searchCheckIn < today) {
            setError('Check-in date cannot be in the past');
            return;
        }
        if (!searchCheckOut) {
            setError('Please select a check-out date');
            return;
        }
        if (searchCheckOut <= searchCheckIn) {
            setError('Check-out must be after check-in');
            return;
        }
        
        setSearching(true);
        setError('');
        
        try {
            await new Promise(resolve => setTimeout(resolve, 1500));
            
            let mockResults = generateMockHotels(searchDestination, searchCheckIn, searchCheckOut, searchRooms);
            
            if (hotelName.trim()) {
                mockResults = mockResults.filter(h => 
                    h.name.toLowerCase().includes(hotelName.toLowerCase())
                );
            }
            
            setResults(mockResults);
            setHasSearched(true);
            
            setSearchFormExpanded(false);
            
            // Only auto-select for individual trips, not groups
            if (!isGroupTrip) {
                const recommended = mockResults.find(h => h.recommended);
                if (recommended) {
                    setSelectedHotels([recommended.id]);
                }
            }
        } catch (err) {
            setError('Search failed. Please try again.');
        } finally {
            setSearching(false);
        }
    };

    const toggleHotelSelection = (hotelId: string) => {
        setSelectedHotels(prev => {
            if (prev.includes(hotelId)) {
                return prev.filter(id => id !== hotelId);
            } else if (prev.length < maxSelections) {
                return [...prev, hotelId];
            } else {
                setError(`You can select up to ${maxSelections} hotels`);
                return prev;
            }
        });
    };

    const handleSaveSelections = async () => {
        if (selectedHotels.length === 0) {
            setError('Please select at least one hotel');
            return;
        }
        
        setSaving(true);
        setError('');
        
        try {
            const selected = results.filter(h => selectedHotels.includes(h.id));
            await onHotelsSelected(selected);
            onClose();
        } catch (err) {
            setError('Failed to save selections. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const filteredResults = results.filter(hotel => {
        if (hotel.pricePerNight < priceRange[0] || hotel.pricePerNight > priceRange[1]) return false;
        if (hotel.reviewScore < minRating) return false;
        if (freeCancellationOnly && !hotel.freeCancellation) return false;
        return true;
    });

    const nights = searchCheckIn && searchCheckOut 
        ? Math.ceil((new Date(searchCheckOut).getTime() - new Date(searchCheckIn).getTime()) / (1000 * 60 * 60 * 24))
        : 0;

    const searchSummary = hasSearched ? (
        `${searchDestination} · ${searchCheckIn} - ${searchCheckOut} · ${searchRooms} room${searchRooms > 1 ? 's' : ''}`
    ) : null;

    return (
        <Dialog 
            open={open} 
            onClose={onClose} 
            maxWidth="md" 
            fullWidth
            fullScreen={isMobile}
            PaperProps={{ 
                sx: { 
                    maxHeight: isMobile ? '100%' : '90vh',
                    m: isMobile ? 0 : 2,
                    borderRadius: isMobile ? 0 : 2,
                } 
            }}
        >
            {/* HEADER */}
            <DialogTitle sx={{ 
                fontWeight: 700, 
                fontFamily: 'Poppins, sans-serif',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid #eee',
                py: 1.5,
                px: 2,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Hotel sx={{ color: '#4A6FA5' }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        Search Hotels
                    </Typography>
                </Box>
                <IconButton onClick={onClose} size="small">
                    <Close />
                </IconButton>
            </DialogTitle>
            
            <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
                {/* SEARCH FORM */}
                <Box sx={{ bgcolor: '#FAFAFA', borderBottom: '1px solid #eee' }}>
                    {/* Group Trip Banner */}
                    {isGroupTrip && groupSize && (
                        <Box sx={{ 
                            bgcolor: '#E3F2FD', 
                            px: 2, 
                            py: 1, 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1,
                            borderBottom: '1px solid #BBDEFB',
                        }}>
                            <GroupIcon sx={{ color: '#1976D2', fontSize: 20 }} />
                            <Typography variant="body2" sx={{ color: '#1976D2', fontWeight: 500 }}>
                                Group Trip: {groupSize} travelers
                            </Typography>
                            {searchRooms >= 10 && (
                                <Chip 
                                    label="Max rooms for instant booking" 
                                    size="small" 
                                    sx={{ ml: 'auto', bgcolor: '#FFF3E0', color: '#E65100', fontSize: '0.7rem', height: 22 }}
                                />
                            )}
                        </Box>
                    )}
                    {/* Collapsed summary */}
                    {hasSearched && !searchFormExpanded && (
                        <Box
                            onClick={() => setSearchFormExpanded(true)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                px: 2,
                                py: 1.5,
                                cursor: 'pointer',
                                '&:hover': { bgcolor: '#f0f0f0' },
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Hotel sx={{ fontSize: 18, color: '#4A6FA5' }} />
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {searchSummary}
                                </Typography>
                            </Box>
                            <Button size="small" startIcon={<Edit sx={{ fontSize: 16 }} />} sx={{ textTransform: 'none' }}>
                                Edit
                            </Button>
                        </Box>
                    )}

                    {/* Expanded form */}
                    <Collapse in={searchFormExpanded}>
                        <Box sx={{ p: 2 }}>
                            {isMobile ? (
                                /* MOBILE LAYOUT */
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    <Autocomplete
                                        freeSolo
                                        options={filterDestinations(searchDestination)}
                                        getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
                                        inputValue={searchDestination}
                                        onInputChange={(_, newValue) => setSearchDestination(newValue)}
                                        onChange={(_, newValue) => {
                                            if (newValue && typeof newValue !== 'string') {
                                                setSearchDestination(newValue.label);
                                            }
                                        }}
                                        PopperComponent={AutocompletePopper}
                                        renderOption={(props, option) => (
                                            <Box component="li" {...props} sx={{ py: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                                                    <Box sx={{ 
                                                        bgcolor: option.type === 'city' ? '#E8F5E9' : option.type === 'region' ? '#E3F2FD' : '#FFF3E0',
                                                        color: option.type === 'city' ? '#2E7D32' : option.type === 'region' ? '#1976D2' : '#E65100',
                                                        px: 0.75, 
                                                        py: 0.25, 
                                                        borderRadius: 1,
                                                        fontSize: '0.6rem',
                                                        fontWeight: 600,
                                                        textTransform: 'uppercase',
                                                    }}>
                                                        {option.type}
                                                    </Box>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                            {option.name}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {option.region}, {option.country}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        )}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                fullWidth
                                                size="small"
                                                label="Destination"
                                                placeholder="City or region"
                                                InputProps={{
                                                    ...params.InputProps,
                                                    startAdornment: (
                                                        <>
                                                            <LocationOn sx={{ mr: 1, color: '#999', fontSize: 20 }} />
                                                            {params.InputProps.startAdornment}
                                                        </>
                                                    ),
                                                }}
                                                sx={{ bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                            />
                                        )}
                                    />
                                    
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Hotel Name (optional)"
                                        value={hotelName}
                                        onChange={(e) => setHotelName(e.target.value)}
                                        placeholder="Search specific hotel"
                                        InputProps={{
                                            startAdornment: <Search sx={{ mr: 1, color: '#999', fontSize: 20 }} />,
                                        }}
                                        sx={{ bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                    />

                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Check-in"
                                            type="date"
                                            value={searchCheckIn}
                                            onChange={(e) => setSearchCheckIn(e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                            inputProps={{ min: today }}
                                            sx={{ bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                        />
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Check-out"
                                            type="date"
                                            value={searchCheckOut}
                                            onChange={(e) => setSearchCheckOut(e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                            inputProps={{ min: searchCheckIn || today }}
                                            sx={{ bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                        />
                                    </Box>

                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Box sx={{ flex: 1 }}>
                                            <StepperInput
                                                value={searchRooms}
                                                onChange={setSearchRooms}
                                                min={1}
                                                max={10}
                                                label="Rooms"
                                                icon={<MeetingRoom sx={{ color: '#999', fontSize: 18 }} />}
                                                compact
                                            />
                                        </Box>
                                        <Box sx={{ flex: 1 }}>
                                            <StepperInput
                                                value={searchGuests}
                                                onChange={setSearchGuests}
                                                min={1}
                                                max={20}
                                                label="Guests"
                                                icon={<Person sx={{ color: '#999', fontSize: 18 }} />}
                                                compact
                                            />
                                        </Box>
                                    </Box>

                                    <Button
                                        fullWidth
                                        variant="contained"
                                        onClick={handleSearch}
                                        disabled={searching}
                                        startIcon={searching ? <CircularProgress size={20} color="inherit" /> : <Search />}
                                        sx={{
                                            height: 48,
                                            bgcolor: '#4A6FA5',
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            fontSize: '1rem',
                                            '&:hover': { bgcolor: '#3D5A8A' },
                                        }}
                                    >
                                        {searching ? 'Searching...' : 'Search Hotels'}
                                    </Button>
                                </Box>
                            ) : (
                                /* DESKTOP LAYOUT - Two rows */
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {/* Row 1: Destination + Hotel Name */}
                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                    <Autocomplete
                                            freeSolo
                                            options={filterDestinations(searchDestination)}
                                            getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
                                            inputValue={searchDestination}
                                            onInputChange={(_, newValue) => setSearchDestination(newValue)}
                                            onChange={(_, newValue) => {
                                                if (newValue && typeof newValue !== 'string') {
                                                    setSearchDestination(newValue.label);
                                                }
                                            }}
                                            PopperComponent={AutocompletePopper}
                                            renderOption={(props, option) => (
                                                <Box component="li" {...props} sx={{ py: 1 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                                                        <Box sx={{ 
                                                            bgcolor: option.type === 'city' ? '#E8F5E9' : option.type === 'region' ? '#E3F2FD' : '#FFF3E0',
                                                            color: option.type === 'city' ? '#2E7D32' : option.type === 'region' ? '#1976D2' : '#E65100',
                                                            px: 0.75, 
                                                            py: 0.25, 
                                                            borderRadius: 1,
                                                            fontSize: '0.6rem',
                                                            fontWeight: 600,
                                                            textTransform: 'uppercase',
                                                        }}>
                                                            {option.type}
                                                        </Box>
                                                        <Box sx={{ flex: 1 }}>
                                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                {option.name}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {option.region}, {option.country}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            )}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    fullWidth
                                                    label="Destination"
                                                    placeholder="City or region"
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        startAdornment: (
                                                            <>
                                                                <LocationOn sx={{ mr: 1, color: '#999' }} />
                                                                {params.InputProps.startAdornment}
                                                            </>
                                                        ),
                                                    }}
                                                    sx={{ bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                                />
                                            )}
                                        />
                                        <TextField
                                            fullWidth
                                            label="Hotel Name (optional)"
                                            value={hotelName}
                                            onChange={(e) => setHotelName(e.target.value)}
                                            placeholder="Search specific hotel"
                                            InputProps={{
                                                startAdornment: <Search sx={{ mr: 1, color: '#999' }} />,
                                            }}
                                            sx={{ bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                        />
                                    </Box>
                                    {/* Row 2: Dates + Rooms + Guests + Search */}
                                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch' }}>
                                        <TextField
                                            label="Check-in"
                                            type="date"
                                            value={searchCheckIn}
                                            onChange={(e) => setSearchCheckIn(e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                            inputProps={{ min: today }}
                                            sx={{ flex: 1, bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                        />
                                        <TextField
                                            label="Check-out"
                                            type="date"
                                            value={searchCheckOut}
                                            onChange={(e) => setSearchCheckOut(e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                            inputProps={{ min: searchCheckIn || today }}
                                            sx={{ flex: 1, bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                        />
                                        <Box sx={{ flex: 1 }}>
                                            <StepperInput
                                                value={searchRooms}
                                                onChange={setSearchRooms}
                                                min={1}
                                                max={10}
                                                label="Rooms"
                                                icon={<MeetingRoom sx={{ color: '#999', fontSize: 18 }} />}
                                            />
                                        </Box>
                                        <Box sx={{ flex: 1 }}>
                                            <StepperInput
                                                value={searchGuests}
                                                onChange={setSearchGuests}
                                                min={1}
                                                max={20}
                                                label="Guests"
                                                icon={<Person sx={{ color: '#999', fontSize: 18 }} />}
                                            />
                                        </Box>
                                        <Button
                                            variant="contained"
                                            onClick={handleSearch}
                                            disabled={searching}
                                            startIcon={searching ? <CircularProgress size={20} color="inherit" /> : <Search />}
                                            sx={{
                                                minWidth: 120,
                                                height: 56,
                                                bgcolor: '#4A6FA5',
                                                borderRadius: 2,
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                '&:hover': { bgcolor: '#3D5A8A' },
                                            }}
                                        >
                                            {searching ? '...' : 'Search'}
                                        </Button>
                                    </Box>
                                </Box>
                            )}
                            
                            {nights > 0 && (
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                    {nights} night{nights > 1 ? 's' : ''} · {searchRooms} room{searchRooms > 1 ? 's' : ''} · {searchGuests} guest{searchGuests > 1 ? 's' : ''}
                                </Typography>
                            )}
                        </Box>
                    </Collapse>
                </Box>

                {/* Error */}
                {error && (
                    <Alert severity="error" sx={{ m: 2, borderRadius: 2 }}>{error}</Alert>
                )}

                {/* RESULTS */}
                <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                    {hasSearched && (
                        <>
                            {/* Results header */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    {filteredResults.length} hotel{filteredResults.length !== 1 ? 's' : ''} found
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Chip
                                        label={`${selectedHotels.length}/${maxSelections}`}
                                        size="small"
                                        color={selectedHotels.length > 0 ? 'primary' : 'default'}
                                        sx={{ bgcolor: selectedHotels.length > 0 ? '#F3E5F5' : undefined, color: selectedHotels.length > 0 ? '#4A6FA5' : undefined }}
                                    />
                                    <Button
                                        size="small"
                                        startIcon={showFilters ? <ExpandLess /> : <FilterList />}
                                        onClick={() => setShowFilters(!showFilters)}
                                        sx={{ textTransform: 'none' }}
                                    >
                                        Filters
                                    </Button>
                                </Box>
                            </Box>

                            {/* Filters */}
                            <Collapse in={showFilters}>
                                <Paper sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: '#FAFAFA' }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={4}>
                                            <Typography variant="caption" color="text.secondary">Price per night</Typography>
                                            <Slider
                                                value={priceRange}
                                                onChange={(_, value) => setPriceRange(value as number[])}
                                                valueLabelDisplay="auto"
                                                valueLabelFormat={(v) => `$${v}`}
                                                min={0}
                                                max={500}
                                                sx={{ color: '#4A6FA5' }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Typography variant="caption" color="text.secondary">Minimum rating</Typography>
                                            <Slider
                                                value={minRating}
                                                onChange={(_, value) => setMinRating(value as number)}
                                                valueLabelDisplay="auto"
                                                min={0}
                                                max={10}
                                                step={0.5}
                                                sx={{ color: '#4A6FA5' }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox 
                                                        checked={freeCancellationOnly}
                                                        onChange={(e) => setFreeCancellationOnly(e.target.checked)}
                                                        sx={{ '&.Mui-checked': { color: '#4A6FA5' } }}
                                                    />
                                                }
                                                label="Free cancellation"
                                            />
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Collapse>

                            {/* Hotel cards */}
                            {filteredResults.map((hotel) => (
                                <Card 
                                    key={hotel.id}
                                    sx={{ 
                                        mb: 1.5, 
                                        borderRadius: 2,
                                        cursor: 'pointer',
                                        border: selectedHotels.includes(hotel.id) ? '2px solid #4A6FA5' : '1px solid #e0e0e0',
                                        bgcolor: selectedHotels.includes(hotel.id) ? '#FDF5FF' : 'white',
                                        transition: 'all 0.2s',
                                        '&:hover': { borderColor: '#4A6FA5' },
                                    }}
                                    onClick={() => toggleHotelSelection(hotel.id)}
                                >
                                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' } }}>
                                        {/* Image */}
                                        <CardMedia
                                            component="img"
                                            sx={{ 
                                                width: { xs: '100%', sm: 160 }, 
                                                height: { xs: 140, sm: 120 }, 
                                                objectFit: 'cover' 
                                            }}
                                            image={hotel.image}
                                            alt={hotel.name}
                                        />
                                        
                                        {/* Content */}
                                        <CardContent sx={{ flex: 1, py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                                                        {hotel.recommended && (
                                                            <Chip 
                                                                icon={<Recommend sx={{ fontSize: 12 }} />}
                                                                label="Best" 
                                                                size="small" 
                                                                sx={{ bgcolor: '#E8F5E9', color: '#2E7D32', fontSize: '0.65rem', height: 18 }}
                                                            />
                                                        )}
                                                        <Rating value={hotel.starRating} size="small" readOnly />
                                                    </Box>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                                                        {hotel.name}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <LocationOn sx={{ fontSize: 12 }} />
                                                        {hotel.location}
                                                    </Typography>
                                                    
                                                    {/* Amenities */}
                                                    <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                                                        {hotel.amenities.slice(0, 4).map((amenity) => (
                                                            <Chip
                                                                key={amenity}
                                                                icon={amenityIcons[amenity]}
                                                                label={amenity}
                                                                size="small"
                                                                sx={{ height: 20, fontSize: '0.65rem', textTransform: 'capitalize' }}
                                                            />
                                                        ))}
                                                    </Box>
                                                    
                                                    {/* Tags */}
                                                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                                        {hotel.freeCancellation && (
                                                            <Typography variant="caption" sx={{ color: '#2E7D32' }}>✓ Free cancellation</Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                                
                                                {/* Price */}
                                                <Box sx={{ textAlign: 'right', minWidth: 80 }}>
                                                    <Chip 
                                                        label={hotel.reviewScore.toFixed(1)}
                                                        size="small"
                                                        sx={{ bgcolor: '#4A6FA5', color: 'white', fontWeight: 700, height: 20, fontSize: '0.7rem', mb: 0.5 }}
                                                    />
                                                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#FF6B35', lineHeight: 1 }}>
                                                        ${hotel.pricePerNight}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        /night
                                                    </Typography>
                                                    {nights > 0 && (
                                                        <>
                                                            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                                                                ${hotel.totalPrice} total
                                                            </Typography>
                                                            {isGroupTrip && groupSize && groupSize > 1 && (
                                                                <Typography variant="caption" sx={{ color: '#1976D2', display: 'block' }}>
                                                                    ~${Math.round(hotel.totalPrice / groupSize)}/person
                                                                </Typography>
                                                            )}
                                                        </>
                                                    )}
                                                </Box>
                                            </Box>
                                        </CardContent>
                                        
                                        {/* Selection indicator - desktop only */}
                                        {!isMobile && (
                                            <Box sx={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                px: 1,
                                                bgcolor: selectedHotels.includes(hotel.id) ? '#4A6FA5' : '#F5F5F5',
                                            }}>
                                                <Checkbox 
                                                    checked={selectedHotels.includes(hotel.id)}
                                                    sx={{ 
                                                        color: selectedHotels.includes(hotel.id) ? 'white' : '#999',
                                                        '&.Mui-checked': { color: 'white' },
                                                    }}
                                                />
                                            </Box>
                                        )}
                                    </Box>
                                </Card>
                            ))}

                            {filteredResults.length === 0 && (
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <Hotel sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
                                    <Typography color="text.secondary">
                                        No hotels match your filters.
                                    </Typography>
                                </Box>
                            )}
                        </>
                    )}

                    {/* Empty state */}
                    {!hasSearched && !searching && (
                        <Box sx={{ textAlign: 'center', py: { xs: 4, sm: 6 } }}>
                            <Hotel sx={{ fontSize: { xs: 48, sm: 64 }, color: '#E1BEE7', mb: 1 }} />
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                                Search for hotels
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Enter your dates above
                            </Typography>
                        </Box>
                    )}
                </Box>
            </DialogContent>
            
            {/* FOOTER */}
            <DialogActions sx={{ p: 2, borderTop: '1px solid #eee', gap: 1 }}>
                <Button onClick={onClose} sx={{ textTransform: 'none', borderRadius: 2 }}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSaveSelections}
                    disabled={saving || selectedHotels.length === 0}
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <CheckCircle />}
                    sx={{
                        bgcolor: '#4A6FA5',
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 3,
                        '&:hover': { bgcolor: '#3D5A8A' },
                    }}
                >
{saving ? 'Saving...' : (isGroupTrip ? `Save ${selectedHotels.length} Option${selectedHotels.length !== 1 ? 's' : ''} for Review` : `Add ${selectedHotels.length} Hotel${selectedHotels.length !== 1 ? 's' : ''}`)}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default HotelSearchDialog;


